import Data from 'data';
import Locale from 'data/locale';
import { itemAddProp } from 'logic/item';
import { itemUpdate } from 'logic/itemReducers';
import { AuraFlags, d2_addpercent } from 'logic/skills/calc';
import { monsterBossList, monsterUniqueList, uniqueMonsterAuras } from './monsterList';
import { monsterLevelBase, monsterLevelBonus, monsterPresetMods } from './picker';

const _L = Locale('ui-calculator');

const umodIndex = {
  minionLife: [1, 2, 3],
  championLife: [4, 5, 6],
  uniqueLife: [7, 8, 9],
  championToHit: 10,
  championDamage: 11,
  minionToHit: 12,
  uniqueToHit: 13,
  minionDamage: 14,
  uniqueDamage: 15,
  minionElemMin: [16, 17, 18],
  minionElemMax: [19, 20, 21],
  championElemMin: [22, 23, 24],
  championElemMax: [25, 26, 27],
  uniqueElemMin: [28, 29, 30],
  uniqueElemMax: [31, 32, 33],
};

const dummyMonster = {
  exp: 100, expn: 100, exph: 100,
  ac: 100, acn: 100, ach: 100,
  a1mind: 100, a1mindn: 100, a1mindh: 100,
  a1maxd: 100, a1maxdn: 100, a1maxdh: 100,
  a1th: 100, a1thn: 100, a1thh: 100,
  drain: 100, drainn: 100, drainh: 100,
  coldeffect: -50, coldeffectn: -40, coldeffecth: -33,
  name: 'Target Dummy',
  velocity: 4,
  run: 4,
  issel: 1,
  isatt: 1,
  sizex: 2,
};

export default function monsterStats(monster, difficulty, playerLevel) {
  const prefix = (Data.info.pd2 || monster.single ? 'l' : '');
  const suffix = ['', 'n', 'h'][difficulty];

  let base = Data.monsters[monster.id], unique;
  if (monster.type === 'super') {
    unique = Data.superUniques[monster.id];
    base = Data.monsters[unique?.class];
  } else if (monster.type === 'boss') {
    unique = Data.superUniques[monsterBossList[monster.id]?.unique];
  } else if (monster.type === 'dummy') {
    base = dummyMonster;
  }
  if (!base) return null;

  const base_level = monster.level_ac || monsterLevelBase(monster, difficulty, playerLevel);
  const level = (monster.revive ? monster.level : base_level + monsterLevelBonus(monster));

  const stats = {};
  const skills = {};
  const items = {};

  stats.maxhp = monster.maxhp;
  stats.experience = base['exp' + suffix] || 0;
  stats.armorclass = base['ac' + suffix] || 0;
  stats.mindamage = base[monster.attack + 'mind' + suffix] || 0;
  stats.maxdamage = base[monster.attack + 'maxd' + suffix] || 0;
  stats.tohit = base[monster.attack + 'th' + suffix] || 0;

  stats.drain = base['drain' + suffix] || 0;
  stats.coldeffect = base['coldeffect' + suffix] || 0;

  stats.damageresist = base['resdm' + suffix] || 0;
  stats.magicresist = base['resma' + suffix] || 0;
  stats.fireresist = base['resfi' + suffix] || 0;
  stats.lightresist = base['resli' + suffix] || 0;
  stats.coldresist = base['resco' + suffix] || 0;
  stats.poisonresist = base['respo' + suffix] || 0;

  stats.damageregen = base.damageregen || 0;

  const lvlac = Data.monLvl?.[base_level][prefix + 'ac' + suffix] || 0;
  const lvlxp = Data.monLvl?.[base_level][prefix + 'xp' + suffix] || 0;
  const lvlth = Data.monLvl?.[level][prefix + 'th' + suffix] || 0;
  const lvldm = Data.monLvl?.[level][prefix + 'dm' + suffix] || 0;
  if (base.noratio) {
    stats.armorclass += lvlac;
    stats.tohit += lvlth;
  } else {
    stats.armorclass = (stats.armorclass * lvlac / 100) | 0;
    stats.tohit = (stats.tohit * lvlth / 100) | 0;
    stats.mindamage = (stats.mindamage * lvldm / 100) | 0;
    stats.maxdamage = (stats.maxdamage * lvldm / 100) | 0;
  }
  stats.experience = (stats.experience * lvlxp / 100) | 0;

  if (base.noshldblock || base.mbl) {
    stats.toblock = base['toblock' + suffix] || 0;
  }

  if (monster.players > 1) {
    stats.maxhp = d2_addpercent(stats.maxhp, 50 * (monster.players - 1));
    stats.mindamage = d2_addpercent(stats.mindamage, 6.25 * (monster.players - 1));
    stats.maxdamage = d2_addpercent(stats.maxdamage, 6.25 * (monster.players - 1));
    stats.tohit = d2_addpercent(stats.tohit, 6.25 * (monster.players - 1));
    stats.experience = d2_addpercent(stats.experience, 50 * (monster.players - 1));
  }

  stats.crit = base.crit || 0;

  for (let i = 1; i <= 3; ++i) {
    if (base[`el${i}mode`]?.toLowerCase() !== monster.attack) continue;
    const elem = base[`el${i}type`];
    let pct = base[`el${i}pct${suffix}`], min = base[`el${i}mind${suffix}`] || 0,
      max = base[`el${i}maxd${suffix}`] || 0, dur = base[`el${i}dur${suffix}`] || 0;
    if (!pct) continue;
    if (elem === 'rand') pct /= 5;
    if (!base.noratio) {
      min *= lvldm / 100;
      max *= lvldm / 100;
    }
    if (elem === 'fire' || elem === 'rand') {
      stats.firemindam = (stats.firemindam || 0) + ((min * pct / 100) | 0);
      stats.firemaxdam = (stats.firemaxdam || 0) + ((max * pct / 100) | 0);
    }
    if (elem === 'ltng' || elem === 'rand') {
      stats.lightmindam = (stats.lightmindam || 0) + ((min * pct / 100) | 0);
      stats.lightmaxdam = (stats.lightmaxdam || 0) + ((max * pct / 100) | 0);
    }
    if (elem === 'mag' || elem === 'rand') {
      stats.magicmindam = (stats.magicmindam || 0) + ((min * pct / 100) | 0);
      stats.magicmaxdam = (stats.magicmaxdam || 0) + ((max * pct / 100) | 0);
    }
    if (elem === 'cold' || elem === 'rand') {
      stats.coldmindam = (stats.coldmindam || 0) + ((min * pct / 100) | 0);
      stats.coldmaxdam = (stats.coldmaxdam || 0) + ((max * pct / 100) | 0);
      stats.coldlength = (stats.coldlength || 0) + ((dur * pct / 100) | 0);
    }
    if (elem === 'pois' || elem === 'rand') {
      stats.poisonmindam = (stats.poisonmindam || 0) + (min | 0);
      stats.poisonmaxdam = (stats.poisonmaxdam || 0) + (max | 0);
      stats.poisonlength = (stats.poisonlength || 0) + ((dur * pct / 100) | 0);
      stats.poison_count = (stats.poison_count || 0) + 1;
    }
  }

  const umod = Data.umodConstants;
  const bonus = {};
  if (monster.type === 'champion') {
    stats.maxhp += (stats.maxhp * umod[umodIndex.championLife[difficulty]] / 100) | 0;
    const factor = Data.difficulty[difficulty].championdamagebonus;
    if (monster.champion === 'berserk') {
      stats.experience *= 5;
      stats.maxhp >>= 2;
      bonus.item_tohit_percent = (3 * umod[umodIndex.championDamage] * factor / 100) | 0;
      bonus.damagepercent = (3 * umod[umodIndex.championDamage] * factor / 100) | 0;
    } else {
      stats.experience *= 3;
      bonus.item_tohit_percent = (umod[umodIndex.championToHit] * factor / 100) | 0;
      bonus.damagepercent = (umod[umodIndex.championDamage] * factor / 100) | 0;
    }
    if (monster.champion === 'champion') {
      bonus.velocitypercent = 20;
    } else if (monster.champion === 'fanatic') {
      stats.armorclass = -70;
      bonus.velocitypercent = 100;
    } else if (monster.champion === 'ghostly') {
      stats.damageresist = 50;
      bonus.velocitypercent = -33;
      bonus.coldmindam = (umod[umodIndex.championElemMin[difficulty]] * lvldm / 100) | 0;
      bonus.coldmaxdam = (umod[umodIndex.championElemMax[difficulty]] * lvldm / 100) | 0;
      bonus.coldlength = 150;
    } else if (monster.champion === 'possessed') {
      stats.maxhp *= 2;
      bonus.velocitypercent = 20;
      bonus.curse_resistance = 100;
    }
  } else if (monster.type === 'unique' || monster.type === 'super' || (monster.type === 'boss' && monsterBossList[monster.id].unique)) {
    stats.experience *= 5;
    stats.maxhp += (stats.maxhp * umod[umodIndex.uniqueLife[difficulty]] / 100) | 0;
  }

  const resists = new Set(['fireresist', 'coldresist', 'lightresist', 'poisonresist', 'magicresist', 'damageresist']);
  const immunities = new Set();
  for (const res of resists) {
    if (stats[res] >= 100) immunities.add(res);
  }
  function add(type, amount, cap) {
    const prev = (stats[type] || 0) + (bonus[type] || 0);
    if (cap && prev >= cap) return;
    if (resists.has(type)) {
      const willCap = (prev + amount >= 100);
      if (immunities.size >= 2 && (immunities.has(type) || willCap)) return;
      bonus[type] = (bonus[type] || 0) + amount;
      if (willCap) immunities.add(type);
    } else {
      bonus[type] = (bonus[type] || 0) + amount;
    }
  }

  const mods = [...(monsterPresetMods(monster.type, monster.id) || []), ...(monster.mods || [])];
  const presetAura = (monster.type === 'super' ? monsterUniqueList[monster.id]?.aura :
    (monster.type === 'boss' && monsterBossList[monster.id]?.aura));
  const presetAuraLevel = (monster.type === 'super' ? monsterUniqueList[monster.id]?.auralevel :
    (monster.type === 'boss' && monsterBossList[monster.id]?.auralevel));
  const udamage = Data.difficulty[difficulty].uniquedamagebonus;
  const emin = umod[umodIndex.uniqueElemMin[difficulty]] * lvldm / 100;
  const emax = umod[umodIndex.uniqueElemMax[difficulty]] * lvldm / 100;
  for (const mod of mods) {
    switch (mod) {
    case 'aura':
      if (presetAura || monster.aura) {
        const id = presetAura || monster.aura;
        const info = uniqueMonsterAuras[id];
        if (info) skills[id] = (skills[id] || 0) + Math.max(presetAuraLevel || 1, (level / info.divide) | 0);
      }
      break;
    case 'cold':
      add('coldresist', 75);
      add('coldmindam', emin | 0);
      add('coldmaxdam', emax | 0);
      add('coldlength', 100 + 5 * level);
      break;
    case 'strong':
      add('item_tohit_percent', (umod[umodIndex.uniqueToHit] * udamage / 100) | 0);
      add('damagepercent', ((umod[umodIndex.uniqueDamage] * udamage / 100) | 0) >> (base.type === 'willowisp' ? 1 : 0));
      break;
    case 'lightning':
      add('lightresist', 75);
      add('lightmindam', emin | 0);
      add('lightmaxdam', emax | 0);
      break;
    case 'manahit':
      add('magicresist', 25); //TODO: or is it 20?
      break;
    case 'poisonhit':
      add('poisonresist', 75);
      add('poisonmindam', emin | 0);
      add('poisonmaxdam', emax | 0);
      add('poisonlength', 300 + 10 * level);
      add('poison_count', 1);
      break;
    case 'stoneskin':
      add('damageresist', 50);
      stats.armorclass *= 2;
      break;
    case 'cursed':
      skills[66] = Math.max(skills[66] || 0, 1 + ((level / 5) | 0));
      break;
    case 'fast':
      add('velocitypercent', 100);
      break;
    case 'fire':
      add('fireresist', 75);
      add('firemindam', emin | 0);
      add('firemaxdam', emax | 0);
      break;
    case 'resist':
      add('coldresist', 40, 100);
      add('fireresist', 40, 100);
      add('lightresist', 40, 100);
      break;
    case 'spectralhit':
      add('firemindam', (emin / 5) | 0);
      add('firemaxdam', (emax / 5) | 0);
      add('lightmindam', (emin / 5) | 0);
      add('lightmaxdam', (emax / 5) | 0);
      add('magicmindam', (emin / 5) | 0);
      add('magicmaxdam', (emax / 5) | 0);
      add('coldmindam', (emin / 5) | 0);
      add('coldmaxdam', (emax / 5) | 0);
      add('coldlength', 40 / 5);
      add('poisonmindam', emin | 0);
      add('poisonmaxdam', emax | 0);
      add('poisonlength', 40 / 5);
      add('poison_count', 1);
      add('coldresist', 20, 75);
      add('fireresist', 20, 75);
      add('lightresist', 20, 75);
      break;
    // no default
    }
  }

  for (let i = 1; i <= 8; ++i) {
    const id = base[`skill${i}`];
    if (id) {
      const level = (skills[id] || 0) + base[`sk${i}lvl`] + Data.difficulty[difficulty].monsterskillbonus;
      if (Data.skills[id]?.srvdofunc === 112) {
        skills[66] = level;
        skills[72] = level;
        skills[82] = level;
        skills[87] = level;
        skills[91] = level;
      } else {
        skills[id] = level;
      }
    }
  }

  const props = Data.monProp[base.monprop];
  if (props) {
    for (let i = 1; i <= 6; ++i) {
      const prop = props[`prop${i}${suffix}`];
      if (!prop) continue;
      const param = props[`par${i}${suffix}`], min = props[`min${i}${suffix}`], max = props[`max${i}${suffix}`];
      itemAddProp(stats, prop, param, min, max, (min + max) >> 1);
    }
  }

  if (monster.items) {
    for (const slot in monster.items) {
      if (typeof monster.items[slot] === 'object') {
        items[slot] = itemUpdate({...monster.items[slot]});
      } else {
        items[slot] = monster.items[slot];
      }
    }
  }

  const undead = !!(base.lundead || base.hundead);
  return {
    unit: 'monster',
    name: Locale.strings[unique?.name || base.namestr] || _L(base.name),
    type: monster.type,
    team: 1,
    level,
    class: unique?.class || monster.id,
    items,
    difficulty,
    skills,
    walkvelocity: base.velocity,
    runvelocity: base.run,
    undead,
    demon: !!base.demon,
    boss: !!base.boss,
    primeevil: !!base.primeevil,
    aura_include: AuraFlags.FindMonsters,
    aura_exclude: (undead ? 0 : AuraFlags.OnlyUndead) | AuraFlags.OnlyCorpses |
      (base.isatt ? 0 : AuraFlags.OnlyAttackable) | (base.issel ? 0 : AuraFlags.OnlySelectable) |
      (base.boss ? AuraFlags.IgnoreBosses : 0) | (base.primeevil ? AuraFlags.IgnorePrimeEvils : 0),
    size: base.sizex,
    stats: Object.assign({
      base: stats,
      monster: bonus,
    }, monster.stats),
  };
}
